import { useCallback, useEffect, useMemo } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import Field from '../../../../../../components/Form/Field'

import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { formatDv360ViewabilityTargetingOptions } from '../../../../lineItemHelpers'

import {
  dv360BulkTargetingWasUpdatedSelector,
  dv360TargetingSelector,
  dv360ViewabilityTargetingOptionsSelector,
  dv360ViewabilityTargetingOptionsWasLoadedSelector,
  getDv360ViewabilityTargetingOptionsIsLoadingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../modules/selectors/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  clearUpdateDv360BulkTargeting,
  getDv360ViewabilityTargetingOptions,
  updateDv360BulkTargeting
} from '../../../../../../modules/actions/dv360Constants'

import { VIEWABILITY } from '../../../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../constants/changeHistory'
import { targetingTypes } from '../../../LineItemDv360Create/fields'
import { getInitialValues } from './fields'

function ViewabilitySectionFrom({ editItemData: item, title, ...formProps }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)
  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)
  const dv360ViewabilityTargetingOptions = useSelector(dv360ViewabilityTargetingOptionsSelector)
  const getDv360ViewabilityTargetingOptionsIsLoading = useSelector(getDv360ViewabilityTargetingOptionsIsLoadingSelector)
  const dv360ViewabilityTargetingOptionsWasLoaded = useSelector(dv360ViewabilityTargetingOptionsWasLoadedSelector)

  const formattedViewabilityTargetingOptions = useMemo(() => {
    return formatDv360ViewabilityTargetingOptions(dv360ViewabilityTargetingOptions)
  }, [dv360ViewabilityTargetingOptions])

  const initialValues = useMemo(() => {
    return getInitialValues(targeting)
  }, [targeting])

  const handleSubmit = useCallback(
    values => {
      const requests = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_VIEWABILITY,
          assigned_targeting_options: values[VIEWABILITY]
            ? [
                {
                  viewability: values[VIEWABILITY]
                }
              ]
            : []
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = formik

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  const clearViewabilityHandler = useCallback(() => {
    setFieldValue(VIEWABILITY, '')
  }, [setFieldValue])

  useEffect(() => {
    if (!dv360ViewabilityTargetingOptionsWasLoaded) {
      dispatch(
        getDv360ViewabilityTargetingOptions({
          account: selectedAdAccountId
        })
      )
    }
  }, [dispatch, selectedAdAccountId, dv360ViewabilityTargetingOptionsWasLoaded])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <Field
        placeholder={t('Viewability')}
        options={formattedViewabilityTargetingOptions}
        name={VIEWABILITY}
        value={values[VIEWABILITY]}
        isLoading={getDv360ViewabilityTargetingOptionsIsLoading}
        formik={formik}
        isClearable={true}
        onClearInput={clearViewabilityHandler}
      />
    </EditForm>
  )
}

export default ViewabilitySectionFrom
